/**
 * TemplateEditor - Main SVG template editor using Konva.js (SaaS Port)
 *
 * Replaces TemplatesTabNew (svgcanvas) with Konva-based canvas.
 * Features:
 * - Fill-container scaling with proper coordinate handling
 * - Drag, resize, rotate shapes with Transformer
 * - Full variations support
 * - Field assignment
 * - SVG import/export
 *
 * PORTED FROM TAURI:
 * - Replaced Tauri file operations with browser downloads
 * - Replaced showToast with console logging
 * - Updated import paths for SaaS structure
 * - Using templateService for database operations
 * - Using WorkspaceContext for workspace ID
 */

import { useEffect, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import {
    Upload, Square, Trash2, Plus, ZoomIn, ZoomOut,
    FolderOpen, Edit2, Check, X, Copy, Lock, Unlock, Undo2, Redo2,
    MousePointer2, Move, PenTool, AlertCircle, FileDown
} from 'lucide-react';
import { KonvaCanvas } from './components/KonvaCanvas';
import { HorizontalRuler, VerticalRuler } from './components/Ruler';
import { useTemplateStore } from './useTemplateStore';
import type {
    CustomizationField,
    TemplateShape,
    ProductTemplate,
    ProductVariation,
    ShapeProcessSettings
} from '@jigflow/shared';
import type { CursorMode } from './useTemplateStore';
import { useProducts } from '../../../contexts/ProductContext';
import { useMachines } from '../../../contexts/MachineContext';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import * as templateService from '../../../services/templateService';
import * as productService from '../../../services/productService';
import { ShapesPanel } from './ShapesPanel';
import ProcessSettingsPanel from './ProcessSettingsPanel';

// Parser utilities (ported from Tauri to shared package)
import { parseSvgFileAsync, scaleShapesToFit } from '@jigflow/shared/parsers/svgImport';
import { parseXcsFile, scaleXcsShapesToFit } from '@jigflow/shared/parsers/xcsImport';
import { parseLbrnFile, scaleLbrnShapesToFit } from '@jigflow/shared/parsers/lbrnImport';
import { generateEmptySvg } from '@jigflow/shared/parsers/svgExport';

// Import generators from shared package
import {
    getLaserTypeFromMachine,
    getLaserPlatform,
    hasMultipleLightSources,
    getLightSourceOptions,
    type LightSourceMode,
    buildXcs
} from '@jigflow/shared/generators/xcsGenerator';
import { buildLbrn } from '@jigflow/shared/generators/lbrnGenerator';

interface TemplateEditorProps {
    productId: string;
}

/** Handle exposed to parent (ProductEditor) via ref */
export interface TemplateEditorHandle {
    saveAll: () => Promise<void>;
    hasUnsavedChanges: () => boolean;
}

/** Undo stack entry includes both shapes and process settings */
interface UndoEntry {
    shapes: TemplateShape[];
    processSettings: Map<string, ShapeProcessSettings[]>;
}

const TemplateEditor = forwardRef<TemplateEditorHandle, TemplateEditorProps>(function TemplateEditor({ productId }, ref) {
    // Get contexts
    const { workspace } = useWorkspace();
    const workspaceId = workspace?.id;

    const {
        getFieldsByProductId,
        getVariationsByProductId,
        getTemplateByProductId,
        getShapesByTemplateId,
        getProcessSettingsByShapeId,
        addField,
        updateField,
        deleteField
    } = useProducts();

    const { machines, activeMachine } = useMachines();

    // Get Zustand store
    const {
        shapes,
        selectedShapeId,
        template,
        variations,
        selectedVariationId,
        cursorMode,
        snapEnabled,
        gridEnabled,
        zoom,
        loading,
        setShapes,
        addShape,
        updateShape,
        deleteShape,
        selectShape,
        setTemplate,
        setVariations,
        addVariation,
        updateVariation,
        deleteVariation,
        selectVariation,
        setCursorMode,
        setSnapEnabled,
        setGridEnabled,
        setZoom,
        setLoading,
        copyShapes,
        getClipboard,
        reset,
        // Process settings panel
        processSettingsPanelExpanded,
        setProcessSettingsPanelExpanded,
        copiedProcessSettings,
        setCopiedProcessSettings,
        activeProcessTab,
        // Snapshot (buffered editing)
        captureSnapshot,
        getChanges,
        hasUnsavedChanges: storeHasUnsavedChanges,
    } = useTemplateStore();

    // Local state for fields and variation editing
    const [fields, setFields] = useState<CustomizationField[]>([]);
    const [variationsPanelOpen, setVariationsPanelOpen] = useState(true);
    const [editingVariationId, setEditingVariationId] = useState<string | null>(null);
    const [editingVariationName, setEditingVariationName] = useState('');
    const [showAddVariation, setShowAddVariation] = useState(false);
    const [newVariationName, setNewVariationName] = useState('');

    // Multi-select state for variations
    const [selectedVariationIds, setSelectedVariationIds] = useState<Set<string>>(new Set());
    const [lastClickedVariationIndex, setLastClickedVariationIndex] = useState<number | null>(null);

    // Aspect ratio lock state - default to locked
    const [aspectLocked, setAspectLocked] = useState(true);

    // Undo/Redo history (includes process settings for full restore)
    const [history, setHistory] = useState<UndoEntry[]>([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [isUndoRedo, setIsUndoRedo] = useState(false);

    // All process settings for all shapes: Map<shapeId, ShapeProcessSettings[]>
    // This is the authoritative local state for buffered editing
    const [allProcessSettings, setAllProcessSettings] = useState<Map<string, ShapeProcessSettings[]>>(new Map());

    // Process settings for selected shape (derived from allProcessSettings)
    const [shapeProcessSettings, setShapeProcessSettings] = useState<Map<string, ShapeProcessSettings>>(new Map());
    // Track which shapes have any process settings (for visual indicator)
    const [shapesWithSettingsSet, setShapesWithSettingsSet] = useState<Set<string>>(new Set());
    // Track output enabled state for all shapes: Map<shapeId, Map<operation, outputEnabled>>
    const [outputEnabledMap, setOutputEnabledMap] = useState<Map<string, Map<string, boolean>>>(new Map());

    // Confirmation modal for clear template
    const [showClearConfirm, setShowClearConfirm] = useState(false);

    // Canvas metrics for rulers (from KonvaCanvas)
    const [canvasMetrics, setCanvasMetrics] = useState({
        scale: 1,
        offsetX: 0,
        offsetY: 0,
        containerWidth: 0,
        containerHeight: 0
    });

    // Get work area dimensions from active machine
    const workAreaWidth = activeMachine?.width || 300;
    const workAreaHeight = activeMachine?.height || 300;

    // Determine if this is a gantry laser (P2, P2S, P3) for lower focus option
    const laserType = activeMachine ? getLaserTypeFromMachine(activeMachine) : 'diode';
    const isGantryLaser = getLaserPlatform(laserType) === 'gantry';

    // Check if this laser has multiple light sources to choose from
    const showLightSourceSelector = hasMultipleLightSources(laserType);
    const lightSourceOptions = getLightSourceOptions(laserType);

    // ============================================================
    // DATA LOADING
    // ============================================================

    useEffect(() => {
        loadData();
        return () => reset();
    }, [productId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [loadedFields, loadedVariations] = await Promise.all([
                getFieldsByProductId(productId),
                getVariationsByProductId(productId)
            ]);

            setFields(loadedFields);
            setVariations(loadedVariations);

            const variationToLoad = loadedVariations.length > 0 ? loadedVariations[0].id : null;
            selectVariation(variationToLoad);

            await loadTemplateForVariation(variationToLoad);
        } catch (error) {
            console.error('[TemplateEditor] Failed to load template data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadTemplateForVariation = async (variationId: string | null) => {
        try {
            const loadedTemplate = await getTemplateByProductId(productId, variationId || undefined);

            if (!loadedTemplate) {
                // Auto-create template (only if we have an active machine and workspace)
                if (!activeMachine || !workspaceId) {
                    setTemplate(null);
                    setShapes([]);
                    captureSnapshot(new Map());
                    return;
                }

                // Template creation on initial load stays — FK reference needed
                const newTemplate = await templateService.saveTemplate(workspaceId, {
                    productId,
                    variationId: variationId || undefined,
                    machineId: activeMachine.id,
                    name: variationId
                        ? `Template for ${variations.find(v => v.id === variationId)?.name || 'Variation'}`
                        : 'Default Template',
                    svgContent: generateEmptySvg(workAreaWidth, workAreaHeight),
                    width: workAreaWidth,
                    height: workAreaHeight
                });
                setTemplate(newTemplate);
                setShapes([]);
                captureSnapshot(new Map());
            } else {
                setTemplate(loadedTemplate);
                const loadedShapes = await getShapesByTemplateId(loadedTemplate.id);
                setShapes(loadedShapes);

                // Load all process settings for snapshot
                const allSettings = new Map<string, ShapeProcessSettings[]>();
                for (const shape of loadedShapes) {
                    const settings = await getProcessSettingsByShapeId(shape.id);
                    if (settings.length > 0) {
                        allSettings.set(shape.id, settings);
                    }
                }
                // Initialize the local shapeProcessSettings map for the selected shape
                // (this will be set by the selectedShapeId effect)
                setAllProcessSettings(allSettings);
                captureSnapshot(allSettings);
            }

            selectShape(null);
        } catch (error) {
            console.error('[TemplateEditor] Failed to load template:', error);
        }
    };

    // ============================================================
    // UNDO/REDO
    // ============================================================

    // Track shapes + process settings changes for undo/redo
    useEffect(() => {
        if (isUndoRedo) {
            setIsUndoRedo(false);
            return;
        }
        // Don't track empty arrays on initial load
        if (shapes.length === 0 && history.length === 0) return;

        // Add current state to history (shapes + process settings)
        setHistory(prev => {
            const newHistory = prev.slice(0, historyIndex + 1);
            // Deep clone both shapes and process settings
            const clonedSettings = new Map<string, ShapeProcessSettings[]>();
            for (const [key, value] of allProcessSettings) {
                clonedSettings.set(key, JSON.parse(JSON.stringify(value)));
            }
            newHistory.push({
                shapes: JSON.parse(JSON.stringify(shapes)),
                processSettings: clonedSettings,
            });
            // Limit history to 50 states
            if (newHistory.length > 50) newHistory.shift();
            return newHistory;
        });
        setHistoryIndex(prev => Math.min(prev + 1, 49));
    }, [shapes]);

    const handleUndo = useCallback(() => {
        if (historyIndex <= 0) return;

        const newIndex = historyIndex - 1;
        const entry = history[newIndex];
        if (!entry) return;

        setIsUndoRedo(true);
        setHistoryIndex(newIndex);
        setShapes(entry.shapes);
        setAllProcessSettings(entry.processSettings);
    }, [historyIndex, history, setShapes]);

    const handleRedo = useCallback(() => {
        if (historyIndex >= history.length - 1) return;

        const newIndex = historyIndex + 1;
        const entry = history[newIndex];
        if (!entry) return;

        setIsUndoRedo(true);
        setHistoryIndex(newIndex);
        setShapes(entry.shapes);
        setAllProcessSettings(entry.processSettings);
    }, [historyIndex, history, setShapes]);

    const canUndo = historyIndex > 0;
    const canRedo = historyIndex < history.length - 1;

    // ============================================================
    // PROCESS SETTINGS
    // ============================================================

    // Derive shapesWithSettings and outputEnabledMap from local allProcessSettings (no DB calls)
    useEffect(() => {
        if (shapes.length === 0) {
            setShapesWithSettingsSet(new Set());
            setOutputEnabledMap(new Map());
            return;
        }
        const settingsSet = new Set<string>();
        const enabledMap = new Map<string, Map<string, boolean>>();
        for (const [shapeId, settingsArray] of allProcessSettings) {
            if (settingsArray.length > 0) {
                settingsSet.add(shapeId);
            }
            const opMap = new Map<string, boolean>();
            for (const s of settingsArray) {
                opMap.set(s.operation, s.outputEnabled !== false);
            }
            enabledMap.set(shapeId, opMap);
        }
        setShapesWithSettingsSet(settingsSet);
        setOutputEnabledMap(enabledMap);
    }, [shapes, allProcessSettings]);

    // Derive shapeProcessSettings for selected shape from local allProcessSettings
    useEffect(() => {
        if (!selectedShapeId) {
            setShapeProcessSettings(new Map());
            return;
        }
        const settings = allProcessSettings.get(selectedShapeId) || [];
        const settingsMap = new Map<string, ShapeProcessSettings>();
        settings.forEach(s => settingsMap.set(s.operation, s));
        setShapeProcessSettings(settingsMap);
    }, [selectedShapeId, allProcessSettings]);

    // Handle process settings changes (local state only — buffered)
    const handleProcessSettingsChange = useCallback((operation: string, updates: Partial<ShapeProcessSettings>) => {
        if (!selectedShapeId) return;

        const existing = shapeProcessSettings.get(operation);
        const updatedSetting: ShapeProcessSettings = {
            id: existing?.id || crypto.randomUUID(),
            shapeId: selectedShapeId,
            operation: operation as 'score' | 'engrave' | 'cut',
            outputEnabled: true,
            ...existing,
            ...updates,
        } as ShapeProcessSettings;

        // Update the per-shape settings map
        setShapeProcessSettings(prev => {
            const newMap = new Map(prev);
            newMap.set(operation, updatedSetting);
            return newMap;
        });

        // Update allProcessSettings
        setAllProcessSettings(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(selectedShapeId) || [];
            const idx = existing.findIndex(s => s.operation === operation);
            if (idx >= 0) {
                const updated = [...existing];
                updated[idx] = updatedSetting;
                newMap.set(selectedShapeId, updated);
            } else {
                newMap.set(selectedShapeId, [...existing, updatedSetting]);
            }
            return newMap;
        });
    }, [selectedShapeId, shapeProcessSettings]);

    // Handle toggle expand for process settings panel
    const handleToggleProcessPanel = useCallback(() => {
        setProcessSettingsPanelExpanded(!processSettingsPanelExpanded);
    }, [processSettingsPanelExpanded, setProcessSettingsPanelExpanded]);

    // Copy current operation's process settings
    const handleCopyProcessSettings = useCallback(() => {
        const currentSettings = shapeProcessSettings.get(activeProcessTab);
        if (currentSettings) {
            setCopiedProcessSettings({ operation: activeProcessTab, settings: currentSettings });
            console.log(`[TemplateEditor] Copied ${activeProcessTab} settings`);
        }
    }, [activeProcessTab, shapeProcessSettings, setCopiedProcessSettings]);

    // Copy all operation process settings
    const handleCopyAllProcessSettings = useCallback(() => {
        if (shapeProcessSettings.size > 0) {
            setCopiedProcessSettings({ all: new Map(shapeProcessSettings) });
            console.log('[TemplateEditor] Copied all process settings');
        }
    }, [shapeProcessSettings, setCopiedProcessSettings]);

    // Paste process settings (local state only — buffered)
    const handlePasteProcessSettings = useCallback(() => {
        if (!copiedProcessSettings || !selectedShapeId) return;

        if ('all' in copiedProcessSettings) {
            // Paste all operations
            const newSettingsList: ShapeProcessSettings[] = [];
            for (const [operation, settings] of copiedProcessSettings.all) {
                const newSetting = {
                    ...settings,
                    id: crypto.randomUUID(),
                    shapeId: selectedShapeId
                } as ShapeProcessSettings;
                newSettingsList.push(newSetting);
                setShapeProcessSettings(prev => {
                    const newMap = new Map(prev);
                    newMap.set(operation, newSetting);
                    return newMap;
                });
            }
            setAllProcessSettings(prev => {
                const newMap = new Map(prev);
                const existing = newMap.get(selectedShapeId) || [];
                // Replace matching operations, keep others
                const merged = [...existing.filter(e => !newSettingsList.some(n => n.operation === e.operation)), ...newSettingsList];
                newMap.set(selectedShapeId, merged);
                return newMap;
            });
            console.log('[TemplateEditor] Pasted all process settings');
        } else {
            // Paste single operation
            const newSetting = {
                ...copiedProcessSettings.settings,
                id: crypto.randomUUID(),
                shapeId: selectedShapeId,
                operation: activeProcessTab
            } as ShapeProcessSettings;
            setShapeProcessSettings(prev => {
                const newMap = new Map(prev);
                newMap.set(activeProcessTab, newSetting);
                return newMap;
            });
            setAllProcessSettings(prev => {
                const newMap = new Map(prev);
                const existing = newMap.get(selectedShapeId) || [];
                const idx = existing.findIndex(s => s.operation === activeProcessTab);
                if (idx >= 0) {
                    const updated = [...existing];
                    updated[idx] = newSetting;
                    newMap.set(selectedShapeId, updated);
                } else {
                    newMap.set(selectedShapeId, [...existing, newSetting]);
                }
                return newMap;
            });
            console.log(`[TemplateEditor] Pasted ${activeProcessTab} settings`);
        }
    }, [copiedProcessSettings, selectedShapeId, activeProcessTab]);

    // Handle per-operation light source change
    const handleLightSourceChange = useCallback((lightSource: LightSourceMode) => {
        // Update the current operation's settings with the new light source
        handleProcessSettingsChange(activeProcessTab, { lightSource });
    }, [activeProcessTab, handleProcessSettingsChange]);

    // Apply process settings to all shapes with same operation enabled (local state only)
    const handleApplyToAllShapes = useCallback(() => {
        const currentSettings = shapeProcessSettings.get(activeProcessTab);
        if (!currentSettings || !selectedShapeId) return;

        // Find all shapes with this operation enabled
        const targetShapes = shapes.filter(s => {
            if (s.id === selectedShapeId) return false;
            if (activeProcessTab === 'score') return s.score === true;
            if (activeProcessTab === 'engrave') return s.engrave !== false;
            if (activeProcessTab === 'cut') return s.cut === true;
            return false;
        });

        if (targetShapes.length === 0) {
            console.log(`[TemplateEditor] No other shapes with ${activeProcessTab} enabled`);
            return;
        }

        setAllProcessSettings(prev => {
            const newMap = new Map(prev);
            for (const shape of targetShapes) {
                const newSetting: ShapeProcessSettings = {
                    ...currentSettings,
                    id: crypto.randomUUID(),
                    shapeId: shape.id
                } as ShapeProcessSettings;
                const existing = newMap.get(shape.id) || [];
                const idx = existing.findIndex(s => s.operation === activeProcessTab);
                if (idx >= 0) {
                    const updated = [...existing];
                    updated[idx] = newSetting;
                    newMap.set(shape.id, updated);
                } else {
                    newMap.set(shape.id, [...existing, newSetting]);
                }
            }
            return newMap;
        });
        console.log(`[TemplateEditor] Applied ${activeProcessTab} settings to ${targetShapes.length} shape${targetShapes.length > 1 ? 's' : ''}`);
    }, [activeProcessTab, shapeProcessSettings, selectedShapeId, shapes]);

    // Get operation color for the current shape and tab
    const getOperationColor = useCallback(() => {
        const selectedShape = shapes.find(s => s.id === selectedShapeId);
        if (!selectedShape) return '#888888';

        switch (activeProcessTab) {
            case 'score': return selectedShape.scoreColor || '#fe0002';
            case 'engrave': return selectedShape.engraveColor || '#000000';
            case 'cut': return selectedShape.cutColor || '#00befe';
            default: return '#888888';
        }
    }, [shapes, selectedShapeId, activeProcessTab]);

    // ============================================================
    // CANVAS METRICS HANDLER (for rulers)
    // ============================================================

    const handleScaleOffsetChange = useCallback((
        scale: number,
        offsetX: number,
        offsetY: number,
        containerWidth: number,
        containerHeight: number
    ) => {
        setCanvasMetrics({ scale, offsetX, offsetY, containerWidth, containerHeight });
    }, []);

    // ============================================================
    // SHAPE HANDLERS
    // ============================================================

    const handleShapeChange = useCallback((id: string, updates: Partial<TemplateShape>) => {
        // Update local state only (buffered — DB write deferred to saveAll)
        updateShape(id, updates);
    }, [updateShape]);

    // Handle drawing a rectangle from canvas (draw mode) — local state only
    const handleDrawRect = (x: number, y: number, width: number, height: number) => {
        if (!template) return;

        // Minimum size check
        if (width < 5 || height < 5) {
            console.log('[TemplateEditor] Rectangle too small (min 5mm)');
            return;
        }

        const newShape: TemplateShape = {
            id: crypto.randomUUID(),
            templateId: template.id,
            type: 'rect',
            x,
            y,
            width,
            height,
            rotation: 0,
            orderIndex: shapes.length,
            fieldId: undefined,
            engrave: false,
            score: false,
            cut: false,
        } as TemplateShape;

        addShape(newShape);
        selectShape(newShape.id);
        setCursorMode('move');
    };

    // Export template as XCS or LBRN file based on machine software
    // Exports field zones as rectangles (no text since none entered)
    // This allows re-importing the template later
    const handleExport = async () => {
        if (!activeMachine) {
            console.error('[TemplateEditor] No active machine selected');
            return;
        }

        if (shapes.length === 0) {
            console.error('[TemplateEditor] No shapes to export');
            return;
        }

        const isLightBurn = activeMachine.software === 'lightburn';

        try {
            let content: string;
            let filename: string;

            if (isLightBurn) {
                // Load process settings for all shapes (for round-trip fidelity)
                const allProcessSettings = new Map<string, Map<string, ShapeProcessSettings>>();
                for (const shape of shapes) {
                    const shapeSettings = await getProcessSettingsByShapeId(shape.id);
                    if (shapeSettings.length > 0) {
                        const operationMap = new Map<string, ShapeProcessSettings>();
                        shapeSettings.forEach(s => operationMap.set(s.operation, s));
                        allProcessSettings.set(shape.id, operationMap);
                    }
                }

                // Build LBRN for LightBurn machines
                content = await buildLbrn(
                    shapes as any,
                    fields as any,
                    {}, // Empty values - field zones export as rects only
                    workAreaWidth,
                    workAreaHeight,
                    {
                        // IMPORTANT: Don't pass controllerType - LBRN files always store speed in mm/s
                        // The default 'dsp' controller type avoids the 60× conversion that 'grbl' would apply
                        processSettings: allProcessSettings.size > 0 ? allProcessSettings as any : undefined
                    }
                );
                filename = `template-${Date.now()}.lbrn`;
            } else {
                // Build XCS for xTool machines
                content = await buildXcs(
                    shapes,
                    fields as any,
                    {}, // Empty values - field zones export as rects only
                    workAreaWidth,
                    workAreaHeight,
                    {
                        laserType: getLaserTypeFromMachine(activeMachine),
                    }
                );
                filename = `template-${Date.now()}.xcs`;
            }

            // Browser download (replace Tauri file save)
            const blob = new Blob([content], { type: 'application/xml' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);

            console.log('[TemplateEditor] Template exported successfully');
        } catch (error) {
            console.error('[TemplateEditor] Failed to export template:', error);
        }
    };

    const handleDeleteSelected = () => {
        if (!selectedShapeId) return;
        // Also remove process settings for this shape from local state
        setAllProcessSettings(prev => {
            const newMap = new Map(prev);
            newMap.delete(selectedShapeId);
            return newMap;
        });
        deleteShape(selectedShapeId);
        console.log('[TemplateEditor] Shape deleted');
    };

    const handleClearTemplate = () => {
        if (!template || shapes.length === 0) return;
        setShowClearConfirm(true);
    };

    const confirmClearTemplate = () => {
        setShowClearConfirm(false);
        if (!template) return;

        setShapes([]);
        setAllProcessSettings(new Map());
        selectShape(null);
        console.log('[TemplateEditor] Template cleared');
    };

    // ============================================================
    // SVG & RASTER IMPORT
    // ============================================================

    const handleUploadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const ext = file.name.split('.').pop()?.toLowerCase();

        if (ext === 'svg') {
            await handleUploadSvg(file);
        } else if (['png', 'jpg', 'jpeg', 'bmp'].includes(ext || '')) {
            const normalizedFormat = ext === 'jpeg' ? 'jpg' : ext as 'png' | 'jpg' | 'bmp';
            await handleUploadRaster(file, normalizedFormat);
        } else {
            console.log('[TemplateEditor] Unsupported file type. Use SVG, PNG, JPG, or BMP.');
        }

        event.target.value = '';
    };

    const handleUploadRaster = async (file: File, format: 'png' | 'jpg' | 'bmp') => {
        if (!template && !activeMachine) {
            console.log('[TemplateEditor] No active machine configured');
            return;
        }
        if (!workspaceId) return;

        try {
            // Read file as base64 data URI
            const base64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });

            // Get image dimensions
            const { width: imgWidth, height: imgHeight } = await new Promise<{ width: number; height: number }>((resolve, reject) => {
                const img = new Image();
                img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
                img.onerror = reject;
                img.src = base64;
            });

            // Calculate scaled dimensions to fit in workarea while preserving aspect ratio
            const aspectRatio = imgWidth / imgHeight;
            let finalWidth: number;
            let finalHeight: number;

            const maxWidth = workAreaWidth * 0.9;
            const maxHeight = workAreaHeight * 0.9;

            if (imgWidth <= maxWidth && imgHeight <= maxHeight) {
                // Convert pixels to mm (assume 96 DPI)
                finalWidth = imgWidth * 25.4 / 96;
                finalHeight = imgHeight * 25.4 / 96;

                if (finalWidth > maxWidth || finalHeight > maxHeight) {
                    if (finalWidth / maxWidth > finalHeight / maxHeight) {
                        finalWidth = maxWidth;
                        finalHeight = maxWidth / aspectRatio;
                    } else {
                        finalHeight = maxHeight;
                        finalWidth = maxHeight * aspectRatio;
                    }
                }
            } else {
                if (aspectRatio > maxWidth / maxHeight) {
                    finalWidth = maxWidth;
                    finalHeight = maxWidth / aspectRatio;
                } else {
                    finalHeight = maxHeight;
                    finalWidth = maxHeight * aspectRatio;
                }
            }

            // Center on canvas
            const x = (workAreaWidth - finalWidth) / 2;
            const y = (workAreaHeight - finalHeight) / 2;

            // Create or ensure template exists in local state
            let currentTemplate = template;
            if (!currentTemplate) {
                // Template creation — needs DB record for FK reference
                currentTemplate = await templateService.saveTemplate(workspaceId, {
                    productId,
                    variationId: selectedVariationId || undefined,
                    machineId: activeMachine!.id,
                    name: file.name.replace(/\.(png|jpg|jpeg|bmp)$/i, ''),
                    svgContent: undefined,
                    width: workAreaWidth,
                    height: workAreaHeight
                });
                setTemplate(currentTemplate);
            }

            // Create raster shape locally with client-generated UUID
            const newShape: TemplateShape = {
                id: crypto.randomUUID(),
                templateId: currentTemplate.id,
                type: 'raster',
                x,
                y,
                width: finalWidth,
                height: finalHeight,
                rotation: 0,
                orderIndex: shapes.length,
                fieldId: undefined,
                rasterData: base64,
                rasterFormat: format,
                rasterOriginalWidth: imgWidth,
                rasterOriginalHeight: imgHeight,
                engrave: false,
                score: false,
                cut: false
            } as TemplateShape;

            addShape(newShape);
            selectShape(newShape.id);
            console.log(`[TemplateEditor] Raster image loaded (${Math.round(finalWidth)}x${Math.round(finalHeight)}mm)`);
        } catch (error) {
            console.error('[TemplateEditor] Failed to import raster image:', error);
        }
    };

    const handleUploadSvg = async (file: File) => {
        if (!workspaceId) return;

        try {
            const svgContent = await file.text();
            const result = await parseSvgFileAsync(svgContent, workAreaWidth, workAreaHeight);

            // Auto-scale if needed
            let shapesToAdd = result.shapes;
            if (result.needsScaling) {
                shapesToAdd = scaleShapesToFit(
                    result.shapes,
                    result.originalSize.width,
                    result.originalSize.height,
                    workAreaWidth,
                    workAreaHeight
                );
            }

            if (shapesToAdd.length === 0) {
                console.log('[TemplateEditor] No shapes to import');
                return;
            }

            // Create or update template locally
            let newTemplate: ProductTemplate;
            const finalSvgContent = result.svgContent;

            if (template) {
                newTemplate = { ...template, svgContent: finalSvgContent };
            } else {
                if (!activeMachine) {
                    console.log('[TemplateEditor] No active machine configured');
                    return;
                }
                // Template creation — needs DB record for FK reference
                newTemplate = await templateService.saveTemplate(workspaceId, {
                    productId,
                    variationId: selectedVariationId || undefined,
                    machineId: activeMachine.id,
                    name: file.name.replace('.svg', ''),
                    svgContent: finalSvgContent,
                    width: workAreaWidth,
                    height: workAreaHeight
                });
            }

            setTemplate(newTemplate);

            // Add shapes to local state with client-generated UUIDs
            for (const shapeData of shapesToAdd) {
                const shape: TemplateShape = {
                    ...shapeData,
                    id: crypto.randomUUID(),
                    templateId: newTemplate.id,
                } as TemplateShape;
                addShape(shape);
            }

            console.log(`[TemplateEditor] SVG loaded. Added ${shapesToAdd.length} shapes.`);
        } catch (error) {
            console.error('[TemplateEditor] Failed to import SVG:', error);
        }
    };

    // ============================================================
    // XCS/LBRN IMPORT
    // ============================================================

    const handleUploadTemplate = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !workspaceId) return;

        const ext = file.name.split('.').pop()?.toLowerCase();

        try {
            const content = await file.text();

            if (ext === 'xcs') {
                const result = await parseXcsFile(content, workAreaWidth, workAreaHeight);

                if (result.warnings.length > 0) {
                    result.warnings.forEach(w => console.warn('[XCS Import]', w));
                }

                let shapesToAdd = result.shapes;
                if (result.needsScaling) {
                    shapesToAdd = scaleXcsShapesToFit(
                        result.shapes,
                        result.originalSize.width,
                        result.originalSize.height,
                        workAreaWidth,
                        workAreaHeight
                    );
                }

                if (shapesToAdd.length === 0) {
                    console.log('[TemplateEditor] No shapes found in XCS file');
                    event.target.value = '';
                    return;
                }

                // Create or reuse template
                let newTemplate: ProductTemplate;
                if (template) {
                    newTemplate = template;
                } else {
                    if (!activeMachine) {
                        console.log('[TemplateEditor] No active machine configured');
                        event.target.value = '';
                        return;
                    }
                    // Template creation — needs DB record for FK reference
                    newTemplate = await templateService.saveTemplate(workspaceId, {
                        productId,
                        variationId: selectedVariationId || undefined,
                        machineId: activeMachine.id,
                        name: file.name.replace('.xcs', ''),
                        svgContent: undefined,
                        width: workAreaWidth,
                        height: workAreaHeight
                    });
                }

                setTemplate(newTemplate);

                // Add shapes to local state with client-generated UUIDs
                const newShapes: TemplateShape[] = [];
                for (const shapeData of shapesToAdd) {
                    const shape: TemplateShape = {
                        ...shapeData,
                        id: crypto.randomUUID(),
                        templateId: newTemplate.id,
                    } as TemplateShape;
                    newShapes.push(shape);
                    addShape(shape);
                }

                // Store process settings locally
                const xcsProcessSettings = result.processSettings;
                if (xcsProcessSettings && xcsProcessSettings.size > 0) {
                    setAllProcessSettings(prev => {
                        const newMap = new Map(prev);
                        for (const [orderIndex, settingsArray] of xcsProcessSettings) {
                            const shape = newShapes[orderIndex];
                            if (!shape) continue;

                            const localSettings: ShapeProcessSettings[] = settingsArray.map(setting => ({
                                id: crypto.randomUUID(),
                                shapeId: shape.id,
                                operation: setting.operation,
                                outputEnabled: setting.outputEnabled ?? true,
                                power: setting.power,
                                speed: setting.speed,
                                passes: setting.passes ?? 1,
                                kerfEnabled: setting.kerfEnabled,
                                kerfDistance: setting.kerfDistance,
                                density: setting.density,
                                scanMode: setting.scanMode as 'zMode' | 'oneWay' | undefined,
                                lightSource: setting.lightSource as 'blue' | 'red' | 'fiber' | 'uv' | undefined,
                                bitmapEngraveMode: setting.bitmapEngraveMode as 'normal' | 'reverse' | undefined,
                                dotDuration: setting.dotDuration,
                                enableBreakPoint: setting.enableBreakPoint,
                                breakPointSize: setting.breakPointSize,
                                breakPointCount: setting.breakPointCount,
                                breakPointMode: setting.breakPointMode as 'count' | 'distance' | undefined,
                                breakPointDistance: setting.breakPointDistance,
                                breakPointPower: setting.breakPointPower,
                            }) as ShapeProcessSettings);
                            newMap.set(shape.id, localSettings);
                        }
                        return newMap;
                    });
                }

                const warningText = result.warnings.length > 0
                    ? ` (${result.warnings.length} warning${result.warnings.length > 1 ? 's' : ''})`
                    : '';
                console.log(`[TemplateEditor] XCS imported. Added ${newShapes.length} shapes${warningText}.`);

            } else if (ext === 'lbrn' || ext === 'lbrn2') {
                const result = await parseLbrnFile(content, workAreaWidth, workAreaHeight);

                if (result.warnings.length > 0) {
                    result.warnings.forEach(w => console.warn('[LBRN Import]', w));
                }

                let shapesToAdd = result.shapes;
                if (result.needsScaling) {
                    shapesToAdd = scaleLbrnShapesToFit(
                        result.shapes,
                        result.originalSize.width,
                        result.originalSize.height,
                        workAreaWidth,
                        workAreaHeight
                    );
                }

                if (shapesToAdd.length === 0) {
                    console.log('[TemplateEditor] No shapes found in LBRN file');
                    event.target.value = '';
                    return;
                }

                // Create or reuse template
                let newTemplate: ProductTemplate;
                if (template) {
                    newTemplate = template;
                } else {
                    if (!activeMachine) {
                        console.log('[TemplateEditor] No active machine configured');
                        event.target.value = '';
                        return;
                    }
                    // Template creation — needs DB record for FK reference
                    newTemplate = await templateService.saveTemplate(workspaceId, {
                        productId,
                        variationId: selectedVariationId || undefined,
                        machineId: activeMachine.id,
                        name: file.name.replace(/\.lbrn2?$/, ''),
                        svgContent: undefined,
                        width: workAreaWidth,
                        height: workAreaHeight
                    });
                }

                setTemplate(newTemplate);

                // Add shapes locally and store process settings from LBRN CutSettings
                const newShapes: TemplateShape[] = [];
                const newSettingsMap = new Map(allProcessSettings);
                for (const shapeData of shapesToAdd) {
                    const shape: TemplateShape = {
                        ...shapeData,
                        id: crypto.randomUUID(),
                        templateId: newTemplate.id,
                    } as TemplateShape;
                    newShapes.push(shape);
                    addShape(shape);

                    // Map LBRN CutSettings to local ShapeProcessSettings
                    const cutIndex = (shapeData as any).lbrnCutIndex;
                    if (cutIndex !== undefined && result.cutSettings) {
                        const cutSetting = result.cutSettings.get(cutIndex);
                        if (cutSetting) {
                            const operation = (cutSetting.type === 'Scan' || cutSetting.type === 'Image') ? 'engrave' : 'cut';
                            const localSetting: ShapeProcessSettings = {
                                id: crypto.randomUUID(),
                                shapeId: shape.id,
                                operation,
                                outputEnabled: cutSetting.doOutput !== false,
                                power: cutSetting.maxPower,
                                speed: cutSetting.speed,
                                passes: cutSetting.numPasses,
                                kerfEnabled: cutSetting.kerf !== undefined && cutSetting.kerf !== 0,
                                kerfDistance: cutSetting.kerf,
                                density: cutSetting.interval ? Math.round(25.4 / cutSetting.interval) : undefined,
                                scanMode: cutSetting.bidir === false ? 'oneWay' : 'zMode',
                                zOffset: cutSetting.zOffset,
                                zPerPass: cutSetting.zPerPass,
                                frequency: cutSetting.frequency,
                                overrideFrequency: cutSetting.overrideFrequency,
                                airAssist: cutSetting.runBlower,
                                enableCutThrough: cutSetting.enableCutThrough,
                                cutThroughPower: cutSetting.throughPower,
                                overcut: cutSetting.overcut,
                                rampLength: cutSetting.rampLength,
                                enablePerforation: cutSetting.perforate,
                                perforationCutLength: cutSetting.perfLen,
                                perforationSkipLength: cutSetting.perfSkip,
                                enableDotMode: cutSetting.dotMode,
                                dotTime: cutSetting.dotTime,
                                dotSpacing: cutSetting.dotSpacing,
                                leadInType: cutSetting.leadInType === 0 ? 'line' : cutSetting.leadInType === 1 ? 'arc' : undefined,
                                leadInLength: cutSetting.leadInLength,
                                leadInAngle: cutSetting.leadInAngle,
                                leadOutType: cutSetting.leadOutType === 0 ? 'line' : cutSetting.leadOutType === 1 ? 'arc' : undefined,
                                leadOutLength: cutSetting.leadOutLength,
                                leadOutAngle: cutSetting.leadOutAngle,
                                scanAngle: cutSetting.angle,
                                crossHatch: cutSetting.crossHatch,
                                floodFill: cutSetting.floodFill,
                                overscanPercent: cutSetting.overscan,
                                lbrnPriority: cutSetting.priority,
                            } as ShapeProcessSettings;
                            const existing = newSettingsMap.get(shape.id) || [];
                            newSettingsMap.set(shape.id, [...existing, localSetting]);
                        }
                    }
                }
                setAllProcessSettings(newSettingsMap);

                const warningText = result.warnings.length > 0
                    ? ` (${result.warnings.length} warning${result.warnings.length > 1 ? 's' : ''})`
                    : '';
                console.log(`[TemplateEditor] LBRN imported. Added ${newShapes.length} shapes${warningText}.`);

            } else {
                console.log('[TemplateEditor] Unsupported format. Use .xcs or .lbrn');
            }

        } catch (error) {
            console.error('[TemplateEditor] Failed to import template:', error);
        }

        event.target.value = '';
    };

    // ============================================================
    // FIELD ASSIGNMENT
    // ============================================================

    const handleFieldAssign = (shapeId: string, fieldId: string | null) => {
        updateShape(shapeId, { fieldId: fieldId || undefined });
        console.log('[TemplateEditor] Field ' + (fieldId ? 'assigned' : 'unassigned'));
    };

    const handleUpdateShapeFromPanel = (shapeId: string, updates: Partial<TemplateShape>) => {
        updateShape(shapeId, updates);
    };

    const handleDeleteShapeFromPanel = (shapeId: string) => {
        setAllProcessSettings(prev => {
            const newMap = new Map(prev);
            newMap.delete(shapeId);
            return newMap;
        });
        deleteShape(shapeId);
        console.log('[TemplateEditor] Shape deleted');
    };

    // Duplicate shape with all its properties and process settings (local state only)
    const handleDuplicateShape = useCallback((shapeId: string) => {
        if (!template) return;

        const shape = shapes.find(s => s.id === shapeId);
        if (!shape) return;

        const newId = crypto.randomUUID();
        const newShape: TemplateShape = {
            ...shape,
            id: newId,
            x: shape.x + 10,
            y: shape.y + 10,
            orderIndex: shapes.length,
        };

        // Copy process settings from original shape
        const originalSettings = allProcessSettings.get(shapeId) || [];
        if (originalSettings.length > 0) {
            const copiedSettings = originalSettings.map(s => ({
                ...s,
                id: crypto.randomUUID(),
                shapeId: newId,
            }));
            setAllProcessSettings(prev => {
                const newMap = new Map(prev);
                newMap.set(newId, copiedSettings);
                return newMap;
            });
        }

        addShape(newShape);
        selectShape(newId);
        console.log('[TemplateEditor] Shape duplicated');
    }, [template, shapes, allProcessSettings, addShape, selectShape]);

    // Copy/Paste handlers
    const handleCopyShape = useCallback(() => {
        if (!selectedShapeId) return;
        const shape = shapes.find(s => s.id === selectedShapeId);
        if (shape) {
            copyShapes([shape]);
            console.log('[TemplateEditor] Shape copied');
        }
    }, [selectedShapeId, shapes, copyShapes]);

    const handlePasteShapes = useCallback(() => {
        const clipboard = getClipboard();
        if (!clipboard || clipboard.length === 0 || !template) return;

        for (const shapeData of clipboard) {
            const newShape: TemplateShape = {
                ...shapeData,
                id: crypto.randomUUID(),
                templateId: template.id,
                orderIndex: shapes.length,
            } as TemplateShape;
            addShape(newShape);
        }
        console.log(`[TemplateEditor] Pasted ${clipboard.length} shape(s)`);
    }, [getClipboard, template, shapes.length, addShape]);

    // Property bar handlers with aspect ratio lock support
    const handlePropertyBarChange = async (property: 'x' | 'y' | 'width' | 'height' | 'rotation', value: number) => {
        if (!selectedShapeId || !workspaceId) return;
        const shape = shapes.find(s => s.id === selectedShapeId);
        if (!shape) return;

        let updates: Partial<TemplateShape> = {};

        // Apply constraints (minimum size only, no workarea boundary limits)
        if (property === 'x') {
            updates.x = value;
        } else if (property === 'y') {
            updates.y = value;
        } else if (property === 'width') {
            const newWidth = Math.max(1, value);
            updates.width = newWidth;
            if (aspectLocked && shape.width > 0) {
                const ratio = shape.height / shape.width;
                updates.height = Math.max(1, newWidth * ratio);
            }
        } else if (property === 'height') {
            const newHeight = Math.max(1, value);
            updates.height = newHeight;
            if (aspectLocked && shape.height > 0) {
                const ratio = shape.width / shape.height;
                updates.width = Math.max(1, newHeight * ratio);
            }
        } else if (property === 'rotation') {
            updates.rotation = value;
        }

        updateShape(selectedShapeId, updates);
    };

    // ============================================================
    // VARIATION MANAGEMENT
    // ============================================================

    const handleSelectVariation = async (variationId: string | null) => {
        if (variationId === selectedVariationId) return;
        selectVariation(variationId);
        setLoading(true);
        await loadTemplateForVariation(variationId);
        setLoading(false);
    };

    const handleAddVariation = async () => {
        if (!newVariationName.trim() || !workspaceId) {
            console.error('[TemplateEditor] Please enter a variation name');
            return;
        }

        try {
            const newVar = await productService.createVariation(workspaceId, {
                productId,
                name: newVariationName.trim(),
                orderIndex: variations.length + 1
            });

            if (variations.length === 0) {
                await addField({
                    productId,
                    label: '__variation_selector__',
                    type: 'select',
                    required: true,
                    options: ['Default', newVariationName.trim()],
                    maxLength: undefined,
                    orderIndex: 0,
                    score: false,
                    engrave: false
                });
                const updatedFields = await getFieldsByProductId(productId);
                setFields(updatedFields);
            } else {
                const selectorField = fields.find(f => f.label === '__variation_selector__');
                if (selectorField) {
                    const currentOptions = Array.isArray(selectorField.options) ? selectorField.options : [];
                    currentOptions.push(newVariationName.trim());
                    await updateField({ ...selectorField, options: currentOptions });
                    const updatedFields = await getFieldsByProductId(productId);
                    setFields(updatedFields);
                }
            }

            addVariation(newVar);
            setNewVariationName('');
            setShowAddVariation(false);
            await handleSelectVariation(newVar.id);
            console.log('[TemplateEditor] Variation created');
        } catch (error) {
            console.error('[TemplateEditor] Failed to create variation:', error);
        }
    };

    const handleSaveVariationEdit = async () => {
        if (!editingVariationId || !editingVariationName.trim()) return;

        try {
            const variation = variations.find(v => v.id === editingVariationId);
            if (!variation) return;

            const oldName = variation.name;
            const newName = editingVariationName.trim();

            await productService.updateVariation(editingVariationId, { name: newName });
            updateVariation(editingVariationId, { name: newName });

            // Update selector field options
            const selectorField = fields.find(f => f.label === '__variation_selector__');
            if (selectorField) {
                const currentOptions = Array.isArray(selectorField.options) ? selectorField.options : [];
                const updatedOptions = currentOptions.map((opt: string) => opt === oldName ? newName : opt);
                await updateField({ ...selectorField, options: updatedOptions });
                const updatedFields = await getFieldsByProductId(productId);
                setFields(updatedFields);
            }

            setEditingVariationId(null);
            setEditingVariationName('');
            console.log('[TemplateEditor] Variation updated');
        } catch (error) {
            console.error('[TemplateEditor] Failed to update variation:', error);
        }
    };

    const handleDeleteVariation = async (variationId: string) => {
        const variation = variations.find(v => v.id === variationId);
        if (!variation || !workspaceId) return;

        if (!confirm(`Delete variation "${variation.name}"? This will also delete its template.`)) return;

        try {
            // Delete template and shapes
            const variationTemplate = await getTemplateByProductId(productId, variationId);
            if (variationTemplate) {
                const templateShapes = await templateService.getShapesByTemplateId(variationTemplate.id);
                for (const shape of templateShapes) {
                    await templateService.deleteShape(shape.id);
                    deleteShape(shape.id);
                }
                await templateService.deleteTemplate(variationTemplate.id);
            }

            await productService.deleteVariation(variationId);
            deleteVariation(variationId);

            // Update selector field
            const selectorField = fields.find(f => f.label === '__variation_selector__');
            const remainingVariations = variations.filter(v => v.id !== variationId);

            if (selectorField) {
                if (remainingVariations.length === 0) {
                    await deleteField(selectorField.id);
                } else {
                    const updatedOptions = ['Default', ...remainingVariations.map(v => v.name)];
                    await updateField({ ...selectorField, options: updatedOptions });
                }
                const updatedFields = await getFieldsByProductId(productId);
                setFields(updatedFields);
            }

            if (selectedVariationId === variationId) {
                const newSelection = remainingVariations.length > 0 ? remainingVariations[0].id : null;
                await handleSelectVariation(newSelection);
            }

            console.log('[TemplateEditor] Variation deleted');
        } catch (error) {
            console.error('[TemplateEditor] Failed to delete variation:', error);
        }
    };

    // Handle variation click with multi-select support
    const handleVariationClick = (variationId: string, index: number, event: React.MouseEvent) => {
        if (event.ctrlKey || event.metaKey) {
            // Ctrl+click: toggle individual selection
            setSelectedVariationIds(prev => {
                const newSet = new Set(prev);
                if (newSet.has(variationId)) {
                    newSet.delete(variationId);
                } else {
                    newSet.add(variationId);
                }
                return newSet;
            });
            setLastClickedVariationIndex(index);
        } else if (event.shiftKey && lastClickedVariationIndex !== null) {
            // Shift+click: select range
            const start = Math.min(lastClickedVariationIndex, index);
            const end = Math.max(lastClickedVariationIndex, index);
            const newSelection = new Set<string>();
            for (let i = start; i <= end; i++) {
                if (variations[i]) {
                    newSelection.add(variations[i].id);
                }
            }
            setSelectedVariationIds(newSelection);
        } else {
            // Normal click: clear selection and select single item for viewing
            setSelectedVariationIds(new Set());
            setLastClickedVariationIndex(index);
            handleSelectVariation(variationId);
        }
    };

    // Bulk delete selected variations
    const handleBulkDeleteVariations = async () => {
        if (selectedVariationIds.size === 0 || !workspaceId) return;

        const count = selectedVariationIds.size;
        if (!confirm(`Delete ${count} selected variation${count > 1 ? 's' : ''}? This will also delete their templates.`)) return;

        try {
            console.log(`[TemplateEditor] Deleting ${count} variations...`);

            for (const variationId of selectedVariationIds) {
                // Delete template and shapes
                const variationTemplate = await getTemplateByProductId(productId, variationId);
                if (variationTemplate) {
                    const templateShapes = await templateService.getShapesByTemplateId(variationTemplate.id);
                    for (const shape of templateShapes) {
                        await templateService.deleteShape(shape.id);
                        deleteShape(shape.id);
                    }
                    await templateService.deleteTemplate(variationTemplate.id);
                }

                await productService.deleteVariation(variationId);
                deleteVariation(variationId);
            }

            // Update selector field with remaining variations
            const selectorField = fields.find(f => f.label === '__variation_selector__');
            const remainingVariations = variations.filter(v => !selectedVariationIds.has(v.id));

            if (selectorField) {
                if (remainingVariations.length === 0) {
                    await deleteField(selectorField.id);
                } else {
                    const updatedOptions = ['Default', ...remainingVariations.map(v => v.name)];
                    await updateField({ ...selectorField, options: updatedOptions });
                }
                const updatedFields = await getFieldsByProductId(productId);
                setFields(updatedFields);
            }

            // Clear selection
            setSelectedVariationIds(new Set());
            setLastClickedVariationIndex(null);

            // Select first remaining variation or null
            const newSelection = remainingVariations.length > 0 ? remainingVariations[0].id : null;
            await handleSelectVariation(newSelection);

            console.log(`[TemplateEditor] Deleted ${count} variation${count > 1 ? 's' : ''}`);
        } catch (error) {
            console.error('[TemplateEditor] Failed to delete variations:', error);
        }
    };

    const handleBulkUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0 || !workspaceId || !activeMachine) return;

        console.log(`[TemplateEditor] Processing ${files.length} files...`);

        let successCount = 0;
        const isFirstBatch = variations.length === 0;

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (!file.name.endsWith('.svg')) continue;

            try {
                const svgContent = await file.text();
                const result = await parseSvgFileAsync(svgContent, workAreaWidth, workAreaHeight);
                const variationName = file.name.replace(/\.svg$/i, '');

                const newVar = await productService.createVariation(workspaceId, {
                    productId,
                    name: variationName,
                    orderIndex: variations.length + i + 1
                });

                if (isFirstBatch && i === 0) {
                    await addField({
                        productId,
                        label: '__variation_selector__',
                        type: 'select',
                        required: true,
                        options: ['Default', variationName],
                        maxLength: undefined,
                        orderIndex: 0,
                        score: false,
                        engrave: false
                    });
                } else {
                    const updatedFields = await getFieldsByProductId(productId);
                    const selectorField = updatedFields.find(f => f.label === '__variation_selector__');
                    if (selectorField) {
                        const currentOptions = Array.isArray(selectorField.options) ? selectorField.options : [];
                        currentOptions.push(variationName);
                        await updateField({ ...selectorField, options: currentOptions });
                    }
                }

                const newTemplate = await templateService.saveTemplate(workspaceId, {
                    productId,
                    variationId: newVar.id,
                    machineId: activeMachine.id,
                    name: variationName,
                    svgContent: result.svgContent,
                    width: workAreaWidth,
                    height: workAreaHeight
                });

                // Scale shapes if needed
                let shapesToAdd = result.shapes;
                if (result.needsScaling) {
                    shapesToAdd = scaleShapesToFit(
                        result.shapes,
                        result.originalSize.width,
                        result.originalSize.height,
                        workAreaWidth,
                        workAreaHeight
                    );
                }

                // Save shapes to database
                for (const shapeData of shapesToAdd) {
                    await templateService.saveShape(workspaceId, { ...shapeData, templateId: newTemplate.id });
                }

                addVariation(newVar);
                successCount++;
            } catch (error) {
                console.error(`[TemplateEditor] Failed to process ${file.name}:`, error);
            }
        }

        // Reload fields and variations
        const updatedFields = await getFieldsByProductId(productId);
        setFields(updatedFields);

        const updatedVariations = await getVariationsByProductId(productId);
        setVariations(updatedVariations);

        if (updatedVariations.length > 0 && successCount > 0) {
            await handleSelectVariation(updatedVariations[variations.length]?.id || null);
        }

        console.log(`[TemplateEditor] Created ${successCount} variations`);
        event.target.value = '';
    };

    // ============================================================
    // SAVE ALL (buffered editing flush)
    // ============================================================

    const saveAllToDb = useCallback(async () => {
        if (!workspaceId) return;

        const changes = getChanges(allProcessSettings);

        // Nothing to save
        if (!changes.shapes.added.length && !changes.shapes.modified.length &&
            !changes.shapes.deleted.length && !changes.settings.added.length &&
            !changes.settings.modified.length && !changes.settings.deleted.length &&
            !changes.templateChanged) {
            console.log('[TemplateEditor] No changes to save');
            return;
        }

        try {
            // 1. Delete removed shapes (cascade deletes their settings via FK)
            for (const shapeId of changes.shapes.deleted) {
                await templateService.deleteShape(shapeId);
            }

            // 2. Update template if changed
            if (changes.templateChanged && template) {
                await templateService.saveTemplate(workspaceId, template);
            }

            // 3. Upsert added shapes (no id in DB yet — saveShape handles insert)
            for (const shape of changes.shapes.added) {
                await templateService.saveShape(workspaceId, shape);
            }

            // 4. Upsert modified shapes
            for (const shape of changes.shapes.modified) {
                await templateService.saveShape(workspaceId, shape);
            }

            // 5. Delete orphaned process settings (settings whose shape still exists but setting was removed)
            // Note: settings on deleted shapes are already cascade-deleted via FK
            // For individually removed settings, delete them via the service
            for (const settingId of changes.settings.deleted) {
                try {
                    await templateService.deleteProcessSettings(settingId);
                } catch {
                    // Ignore — may have been cascade-deleted with parent shape
                }
            }

            // 6. Upsert added/modified settings
            for (const setting of [...changes.settings.added, ...changes.settings.modified]) {
                await templateService.saveProcessSettings(workspaceId, setting);
            }

            // 7. Reset snapshot baseline
            captureSnapshot(allProcessSettings);

            console.log(`[TemplateEditor] Saved: ${changes.shapes.added.length} added, ${changes.shapes.modified.length} modified, ${changes.shapes.deleted.length} deleted`);
        } catch (error) {
            console.error('[TemplateEditor] saveAll failed:', error);
            throw error; // Let caller handle (ProductEditor shows error)
        }
    }, [workspaceId, template, getChanges, allProcessSettings, captureSnapshot]);

    // Expose saveAll and hasUnsavedChanges to parent via ref
    useImperativeHandle(ref, () => ({
        saveAll: saveAllToDb,
        hasUnsavedChanges: () => storeHasUnsavedChanges(allProcessSettings),
    }), [saveAllToDb, storeHasUnsavedChanges, allProcessSettings]);

    // ============================================================
    // KEYBOARD SHORTCUTS
    // ============================================================

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Don't handle if typing in input
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
                return;
            }

            if (e.key === 'Delete' || e.key === 'Backspace') {
                if (selectedShapeId) {
                    e.preventDefault();
                    handleDeleteSelected();
                }
            }

            // Undo: Ctrl+Z
            if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
                e.preventDefault();
                handleUndo();
            }

            // Redo: Ctrl+Y or Ctrl+Shift+Z
            if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
                e.preventDefault();
                handleRedo();
            }

            // Copy: Ctrl+C
            if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
                e.preventDefault();
                handleCopyShape();
            }

            // Paste: Ctrl+V
            if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
                e.preventDefault();
                handlePasteShapes();
            }

            // Arrow key movement for selected shapes
            if (!selectedShapeId || !workspaceId) return;

            const delta = e.shiftKey ? 1 : 0.1; // 1mm or 0.1mm
            const shape = shapes.find(s => s.id === selectedShapeId);
            if (!shape) return;

            let updates: Partial<TemplateShape> | null = null;

            switch (e.key) {
                case 'ArrowLeft':
                    e.preventDefault();
                    updates = { x: shape.x - delta };
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    updates = { x: shape.x + delta };
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    updates = { y: shape.y - delta };
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    updates = { y: shape.y + delta };
                    break;
            }

            if (updates) {
                updateShape(selectedShapeId, updates);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedShapeId, workspaceId, shapes, updateShape, handleUndo, handleRedo, handleCopyShape, handlePasteShapes]);

    // ============================================================
    // RENDER
    // ============================================================

    if (loading) {
        return (
            <div className="h-full flex items-center justify-center">
                <div className="text-slate-400">Loading template...</div>
            </div>
        );
    }

    // Block template editing if no active machine is configured
    if (!activeMachine) {
        return (
            <div className="h-full flex items-center justify-center p-8">
                <div className="text-center max-w-md">
                    <AlertCircle className="mx-auto mb-4 text-amber-500" size={48} />
                    <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-2">
                        No Active Machine
                    </h3>
                    <p className="text-slate-600 dark:text-slate-400 mb-4">
                        Please configure and activate a laser machine before creating templates.
                        Templates are machine-specific to ensure proper sizing and laser settings.
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-500">
                        Go to Settings → Machines to add or activate a machine.
                    </p>
                </div>
            </div>
        );
    }

    // Get selected shape for property bar
    const selectedShape = shapes.find(s => s.id === selectedShapeId);

    return (
        <div className="h-full flex flex-col overflow-hidden">
            {/* Toolbar - Two rows */}
            <div className="flex flex-col border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
                {/* Row 1 - Main tools */}
                <div className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 dark:bg-slate-900">
                    {/* Import & Export */}
                    <div className="flex items-center gap-1 pr-2 border-r border-slate-200 dark:border-slate-700">
                        <label className="flex items-center gap-1 px-1.5 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded cursor-pointer transition" title="Upload SVG or raster image (PNG, JPG, BMP)">
                            <Upload size={14} />
                            <span>Upload</span>
                            <input
                                type="file"
                                accept=".svg,.png,.jpg,.jpeg,.bmp"
                                className="hidden"
                                onChange={handleUploadFile}
                            />
                        </label>
                        <label className="flex items-center gap-1 px-1.5 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded cursor-pointer transition" title="Import XCS/LBRN template">
                            <FileDown size={14} />
                            <span>Import</span>
                            <input
                                type="file"
                                accept=".xcs,.lbrn,.lbrn2"
                                className="hidden"
                                onChange={handleUploadTemplate}
                            />
                        </label>
                        <button
                            onClick={handleExport}
                            disabled={!template || shapes.length === 0}
                            className="flex items-center gap-1 px-1.5 py-1 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 dark:disabled:bg-slate-600 text-white disabled:text-slate-500 text-xs font-medium rounded transition disabled:cursor-not-allowed"
                            title={activeMachine?.software === 'lightburn' ? 'Export as LBRN file' : 'Export as XCS file'}
                        >
                            <FileDown size={14} />
                            Export
                        </button>
                        <button
                            onClick={handleClearTemplate}
                            disabled={!template || shapes.length === 0}
                            className="p-1.5 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Clear all shapes"
                        >
                            <Trash2 size={14} />
                        </button>
                    </div>

                    {/* Cursor modes: Select / Move / Draw */}
                    <div className="flex items-center gap-0.5 pr-2 border-r border-slate-200 dark:border-slate-700">
                        <button
                            onClick={() => setCursorMode('select')}
                            disabled={!template}
                            className={`p-1.5 rounded transition ${cursorMode === 'select'
                                ? 'bg-blue-600 text-white'
                                : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700'
                            } disabled:opacity-30 disabled:cursor-not-allowed`}
                            title="Select mode (click to select)"
                        >
                            <MousePointer2 size={16} />
                        </button>
                        <button
                            onClick={() => setCursorMode('move')}
                            disabled={!template}
                            className={`p-1.5 rounded transition ${cursorMode === 'move'
                                ? 'bg-blue-600 text-white'
                                : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700'
                            } disabled:opacity-30 disabled:cursor-not-allowed`}
                            title="Move mode (move, resize, rotate)"
                        >
                            <Move size={16} />
                        </button>
                        <button
                            onClick={() => setCursorMode('draw')}
                            disabled={!template}
                            className={`p-1.5 rounded transition ${cursorMode === 'draw'
                                ? 'bg-green-600 text-white'
                                : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700'
                            } disabled:opacity-30 disabled:cursor-not-allowed`}
                            title="Draw rectangle mode"
                        >
                            <PenTool size={16} />
                        </button>
                    </div>

                    {/* Undo/Redo */}
                    <div className="flex items-center gap-0.5 pr-2 border-r border-slate-200 dark:border-slate-700">
                        <button
                            onClick={handleUndo}
                            disabled={!canUndo}
                            className="p-1.5 text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700 rounded transition disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Undo (Ctrl+Z)"
                        >
                            <Undo2 size={14} />
                        </button>
                        <button
                            onClick={handleRedo}
                            disabled={!canRedo}
                            className="p-1.5 text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700 rounded transition disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Redo (Ctrl+Y)"
                        >
                            <Redo2 size={14} />
                        </button>
                    </div>

                    {/* Snap & Grid toggles */}
                    <div className="flex items-center gap-3 pr-2 border-r border-slate-200 dark:border-slate-700">
                        <label className="flex items-center gap-1.5 cursor-pointer" title="Snap to center and other shapes">
                            <input
                                type="checkbox"
                                checked={snapEnabled}
                                onChange={(e) => setSnapEnabled(e.target.checked)}
                                className="w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-xs text-slate-600 dark:text-slate-400">Snap</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer" title="Show grid overlay">
                            <input
                                type="checkbox"
                                checked={gridEnabled}
                                onChange={(e) => setGridEnabled(e.target.checked)}
                                className="w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-xs text-slate-600 dark:text-slate-400">Grid</span>
                        </label>
                    </div>

                    {/* Zoom controls */}
                    <div className="flex items-center gap-1 ml-auto">
                        <button
                            onClick={() => setZoom(Math.max(0.25, zoom / 1.2))}
                            className="p-1 text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700 rounded transition"
                            title="Zoom out"
                        >
                            <ZoomOut size={14} />
                        </button>
                        <button
                            onClick={() => setZoom(1)}
                            className="px-1.5 py-0.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition min-w-[3rem] text-center"
                            title="Reset zoom to 100%"
                        >
                            {Math.round(zoom * 100)}%
                        </button>
                        <button
                            onClick={() => setZoom(Math.min(5, zoom * 1.2))}
                            className="p-1 text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700 rounded transition"
                            title="Zoom in"
                        >
                            <ZoomIn size={14} />
                        </button>
                    </div>
                </div>

                {/* Row 2 - Shape properties (when selected) */}
                {selectedShape && (
                    <div className="flex items-center gap-2 px-2 py-1 bg-slate-100 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
                        {/* Position */}
                        <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">
                                <span className="text-xs text-slate-500 dark:text-slate-400">X</span>
                                <input
                                    type="number"
                                    value={Math.round(selectedShape.x * 10) / 10}
                                    onChange={(e) => handlePropertyBarChange('x', parseFloat(e.target.value) || 0)}
                                    className="w-14 px-1.5 py-0.5 text-xs bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded text-slate-800 dark:text-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                            </div>
                            <div className="flex items-center gap-1">
                                <span className="text-xs text-slate-500 dark:text-slate-400">Y</span>
                                <input
                                    type="number"
                                    value={Math.round(selectedShape.y * 10) / 10}
                                    onChange={(e) => handlePropertyBarChange('y', parseFloat(e.target.value) || 0)}
                                    className="w-14 px-1.5 py-0.5 text-xs bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded text-slate-800 dark:text-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                            </div>
                        </div>

                        <div className="w-px h-4 bg-slate-300 dark:bg-slate-600" />

                        {/* Size with aspect lock */}
                        <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">
                                <span className="text-xs text-slate-500 dark:text-slate-400">W</span>
                                <input
                                    type="number"
                                    value={Math.round(selectedShape.width * 10) / 10}
                                    onChange={(e) => handlePropertyBarChange('width', parseFloat(e.target.value) || 0)}
                                    className="w-14 px-1.5 py-0.5 text-xs bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded text-slate-800 dark:text-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                            </div>
                            <button
                                onClick={() => setAspectLocked(!aspectLocked)}
                                className={`p-1 rounded transition ${aspectLocked
                                    ? 'text-blue-600 bg-blue-100 dark:bg-blue-900/30'
                                    : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700'
                                }`}
                                title={aspectLocked ? 'Unlock aspect ratio' : 'Lock aspect ratio'}
                            >
                                {aspectLocked ? <Lock size={12} /> : <Unlock size={12} />}
                            </button>
                            <div className="flex items-center gap-1">
                                <span className="text-xs text-slate-500 dark:text-slate-400">H</span>
                                <input
                                    type="number"
                                    value={Math.round(selectedShape.height * 10) / 10}
                                    onChange={(e) => handlePropertyBarChange('height', parseFloat(e.target.value) || 0)}
                                    className="w-14 px-1.5 py-0.5 text-xs bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded text-slate-800 dark:text-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                            </div>
                        </div>

                        <div className="w-px h-4 bg-slate-300 dark:bg-slate-600" />

                        {/* Rotation */}
                        <div className="flex items-center gap-1">
                            <span className="text-xs text-slate-500 dark:text-slate-400">°</span>
                            <input
                                type="number"
                                value={Math.round(selectedShape.rotation || 0)}
                                onChange={(e) => handlePropertyBarChange('rotation', parseFloat(e.target.value) || 0)}
                                className="w-12 px-1.5 py-0.5 text-xs bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded text-slate-800 dark:text-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                        </div>

                        <div className="w-px h-4 bg-slate-300 dark:bg-slate-600" />

                        {/* Actions */}
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => selectedShapeId && handleDuplicateShape(selectedShapeId)}
                                className="p-1 text-slate-600 hover:bg-slate-200 dark:text-slate-400 dark:hover:bg-slate-600 rounded transition"
                                title="Duplicate shape"
                            >
                                <Copy size={14} />
                            </button>
                            <button
                                onClick={handleDeleteSelected}
                                className="p-1 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition"
                                title="Delete selected (Del)"
                            >
                                <Trash2 size={14} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Main content */}
            <div className="flex-1 flex overflow-hidden min-h-0">
                {/* Variations Panel - Collapsible */}
                <div className="flex flex-shrink-0">
                    {/* Expanded panel */}
                    {variationsPanelOpen && (
                        <div className="w-44 border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-col overflow-hidden">
                            <div className="p-2 space-y-2 overflow-y-auto flex-1">
                                {/* Header */}
                                <div className="space-y-1">
                                    <h3 className="font-semibold text-xs text-slate-800 dark:text-white flex items-center gap-1">
                                        <FolderOpen size={14} />
                                        Variations
                                        {selectedVariationIds.size > 0 && (
                                            <span className="text-[10px] font-normal text-blue-600 dark:text-blue-400">
                                                ({selectedVariationIds.size})
                                            </span>
                                        )}
                                    </h3>
                                    {/* Toolbar - icon only */}
                                    <div className="flex items-center gap-0.5">
                                        {selectedVariationIds.size > 0 && (
                                            <button
                                                onClick={handleBulkDeleteVariations}
                                                className="p-1 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition"
                                                title={`Delete ${selectedVariationIds.size} selected`}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                        <label
                                            className="p-1 text-purple-600 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded transition cursor-pointer"
                                            title="Bulk upload SVGs"
                                        >
                                            <Copy size={14} />
                                            <input
                                                type="file"
                                                accept=".svg"
                                                multiple
                                                className="hidden"
                                                onChange={handleBulkUpload}
                                            />
                                        </label>
                                        <button
                                            onClick={() => setShowAddVariation(true)}
                                            className="p-1 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded transition"
                                            title="Add variation"
                                        >
                                            <Plus size={14} />
                                        </button>
                                    </div>
                                </div>

                                {/* Add variation input */}
                                {showAddVariation && (
                                    <div className="flex items-center gap-1 p-1 bg-slate-100 dark:bg-slate-700 rounded">
                                        <input
                                            type="text"
                                            value={newVariationName}
                                            onChange={(e) => setNewVariationName(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') handleAddVariation();
                                                if (e.key === 'Escape') { setShowAddVariation(false); setNewVariationName(''); }
                                            }}
                                            placeholder="Name..."
                                            className="flex-1 px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 dark:text-white"
                                            autoFocus
                                        />
                                        <button onClick={handleAddVariation} className="p-1 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 rounded">
                                            <Check size={14} />
                                        </button>
                                        <button onClick={() => { setShowAddVariation(false); setNewVariationName(''); }} className="p-1 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-600 rounded">
                                            <X size={14} />
                                        </button>
                                    </div>
                                )}

                                {/* Default template - always shown */}
                                <button
                                    onClick={() => handleSelectVariation(null)}
                                    className={`w-full text-left p-2 rounded text-xs transition ${selectedVariationId === null
                                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
                                        : 'hover:bg-slate-100 dark:hover:bg-slate-700/50 text-slate-600 dark:text-slate-300'
                                        }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <Square size={14} />
                                        <span>Default</span>
                                    </div>
                                </button>

                                {/* Variations list - Ctrl+click to multi-select, Shift+click for range */}
                                <div className="space-y-1">
                                    {variations.map((variation, idx) => {
                                        const isMultiSelected = selectedVariationIds.has(variation.id);
                                        const isActive = selectedVariationId === variation.id;
                                        return (
                                        <div
                                            key={variation.id}
                                            className={`group rounded text-xs transition ${isMultiSelected
                                                ? 'bg-purple-100 dark:bg-purple-900/40 ring-1 ring-purple-400'
                                                : isActive
                                                    ? 'bg-blue-100 dark:bg-blue-900/30'
                                                    : 'hover:bg-slate-100 dark:hover:bg-slate-700/50'
                                                }`}
                                        >
                                            {editingVariationId === variation.id ? (
                                                <div className="flex items-center gap-1 p-1">
                                                    <input
                                                        type="text"
                                                        value={editingVariationName}
                                                        onChange={(e) => setEditingVariationName(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') handleSaveVariationEdit();
                                                            if (e.key === 'Escape') { setEditingVariationId(null); setEditingVariationName(''); }
                                                        }}
                                                        className="flex-1 px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 dark:text-white"
                                                        autoFocus
                                                    />
                                                    <button onClick={handleSaveVariationEdit} className="p-1 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 rounded">
                                                        <Check size={12} />
                                                    </button>
                                                    <button onClick={() => { setEditingVariationId(null); setEditingVariationName(''); }} className="p-1 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-600 rounded">
                                                        <X size={12} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="flex items-center">
                                                    <button
                                                        onClick={(e) => handleVariationClick(variation.id, idx, e)}
                                                        className={`flex-1 text-left p-2 ${isMultiSelected
                                                            ? 'text-purple-700 dark:text-purple-300 font-medium'
                                                            : isActive
                                                                ? 'text-blue-700 dark:text-blue-300 font-medium'
                                                                : 'text-slate-600 dark:text-slate-300'
                                                            }`}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <span className={`w-4 h-4 flex items-center justify-center rounded text-[10px] font-bold ${isMultiSelected
                                                                ? 'bg-purple-500 text-white'
                                                                : 'bg-slate-200 dark:bg-slate-600'
                                                                }`}>
                                                                {isMultiSelected ? '✓' : idx + 1}
                                                            </span>
                                                            <span className="truncate">{variation.name}</span>
                                                        </div>
                                                    </button>
                                                    <div className="flex items-center opacity-0 group-hover:opacity-100 transition pr-1">
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); setEditingVariationId(variation.id); setEditingVariationName(variation.name); }}
                                                            className="p-1 text-slate-500 hover:text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded"
                                                            title="Edit name"
                                                        >
                                                            <Edit2 size={12} />
                                                        </button>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleDeleteVariation(variation.id); }}
                                                            className="p-1 text-slate-500 hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                                                            title="Delete"
                                                        >
                                                            <Trash2 size={12} />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                    })}
                                </div>

                                {variations.length === 0 && (
                                    <p className="text-xs text-slate-400 italic text-center py-2">
                                        Add variations for products with multiple designs (e.g., monogram letters).
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Toggle button - on right side of variations panel */}
                    <button
                        onClick={() => setVariationsPanelOpen(!variationsPanelOpen)}
                        className={`w-8 flex-shrink-0 border-r border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${variationsPanelOpen ? 'bg-slate-50 dark:bg-slate-800' : ''}`}
                        title={variationsPanelOpen ? 'Hide Variations' : 'Show Variations'}
                    >
                        <Copy size={16} className="text-purple-500" />
                    </button>
                </div>

                {/* Canvas area with rulers */}
                <div className="flex-1 flex flex-col min-w-0 min-h-0">
                    {/* Horizontal ruler */}
                    <HorizontalRuler
                        length={workAreaWidth}
                        scale={canvasMetrics.scale}
                        offset={canvasMetrics.offsetX - 20}
                        containerSize={canvasMetrics.containerWidth}
                    />
                    <div className="flex-1 flex min-h-0">
                        {/* Vertical ruler */}
                        <VerticalRuler
                            length={workAreaHeight}
                            scale={canvasMetrics.scale}
                            offset={canvasMetrics.offsetY}
                            containerSize={canvasMetrics.containerHeight}
                        />
                        {/* Canvas */}
                        <KonvaCanvas
                            shapes={shapes}
                            selectedId={selectedShapeId}
                            workAreaWidth={workAreaWidth}
                            workAreaHeight={workAreaHeight}
                            fields={fields}
                            snapEnabled={snapEnabled}
                            cursorMode={cursorMode}
                            zoom={zoom}
                            gridEnabled={gridEnabled}
                            onSelect={selectShape}
                            onShapeChange={handleShapeChange}
                            onDrawRect={handleDrawRect}
                            onZoomChange={setZoom}
                            onScaleOffsetChange={handleScaleOffsetChange}
                        />
                    </div>
                </div>

                {/* Process Settings Panel */}
                <ProcessSettingsPanel
                    shape={selectedShape || null}
                    processSettings={shapeProcessSettings}
                    isExpanded={processSettingsPanelExpanded}
                    onToggleExpand={handleToggleProcessPanel}
                    onSettingsChange={handleProcessSettingsChange}
                    isGantryLaser={isGantryLaser}
                    maxSpeed={activeMachine?.maxSpeed}
                    software={activeMachine?.software}
                    onCopy={handleCopyProcessSettings}
                    onCopyAll={handleCopyAllProcessSettings}
                    onPaste={handlePasteProcessSettings}
                    canPaste={!!copiedProcessSettings}
                    onApplyToAll={handleApplyToAllShapes}
                    operationColor={getOperationColor()}
                    showLightSource={showLightSourceSelector}
                    lightSourceOptions={lightSourceOptions}
                    onLightSourceChange={handleLightSourceChange}
                />

                {/* Shapes Panel */}
                <ShapesPanel
                    fields={fields}
                    workAreaWidth={workAreaWidth}
                    workAreaHeight={workAreaHeight}
                    software={activeMachine?.software}
                    onFieldAssign={handleFieldAssign}
                    onDeleteShape={handleDeleteShapeFromPanel}
                    onUpdateShape={handleUpdateShapeFromPanel}
                    onDuplicateShape={handleDuplicateShape}
                    shapesWithSettings={shapesWithSettingsSet}
                    outputEnabledMap={outputEnabledMap}
                />
            </div>

            {/* Clear Template Confirmation Modal */}
            {showClearConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-sm mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                                <AlertCircle className="text-red-600 dark:text-red-400" size={20} />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Clear Template?</h3>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                            This will delete all {shapes.length} shape{shapes.length !== 1 ? 's' : ''} from the template. This cannot be undone.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowClearConfirm(false)}
                                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmClearTemplate}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition"
                            >
                                Clear All
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

export default TemplateEditor;
